import {
  AllCommentsProps,
  BusniessOfficeNote,
} from "../../../data-model/busniessOfficeNote";
import { BusniessOfficeReportType } from "../../../enums/busniessOfficeReportType";
import { isExceptionFlowForUrologyOrg } from "../../../enums/organizationTypes";
import {
  checkIfDatesAreSame,
  normalizeAuthDatesToSlashFormat,
} from "../../../utils/formatDate";

/**
 * Represents a single comment with metadata for business office notes.
 */
interface CommentItem {
  id: string;
  originalComment: string;
  boStatus?: string;
  writeBackComment?: string;
  showWriteBackNote?: boolean;
  isVisitDateDifferentFromPostTextDate?: boolean;
  commentDate: string;
  type: BusniessOfficeReportType;
  writeStatus: string;
  index?: number;
}

/**
 * Represents a collection of comments returned from note creation functions.
 */
interface CommentsCollection {
  comments: CommentItem[];
}

/**
 * Aggregates all comments from post notes, pre notes, and visit data into a single array.
 *
 * @param {CommentsCollection} postNotes - The post notes collection.
 * @param {CommentsCollection} preNotes - The pre notes collection.
 * @param {CommentsCollection} visitData - The visit data collection.
 * @returns {CommentItem[]} Flattened array of all comments.
 */
const aggregateAllComments = (
  postNotes: CommentsCollection,
  preNotes: CommentsCollection,
  visitData: CommentsCollection,
): CommentItem[] => {
  return [postNotes, preNotes, visitData].flatMap(
    (collection) => collection.comments,
  );
};

/**
 * Filters comments to include only those with a non-empty comment date.
 *
 * @param {CommentItem[]} comments - Array of comments to filter.
 * @returns {CommentItem[]} Filtered array containing only comments with dates.
 */
const filterCommentsWithDates = (comments: CommentItem[]): CommentItem[] => {
  return comments.filter((comment) => comment.commentDate !== "");
};

/**
 * Groups comments by their comment date and determines the appropriate type
 * based on the date of service.
 *
 * Preserves mapped UI fields (including ``writeStatus``) when rebuilding each
 * comment for the grouped context payload.
 *
 * @param sortedComments - Array of comments sorted by date.
 * @param dateOfService - The date of service for type determination.
 * @returns Array of grouped comments by date.
 */
const groupCommentsByDate = (
  sortedComments: CommentItem[],
  dateOfService: string,
): AllCommentsProps[] => {
  const groupedRecord = sortedComments.reduce(
    (acc, comment) => {
      const date = comment.commentDate;

      if (!acc[date]) {
        acc[date] = {
          comments: [],
          commentDate: date,
          type: determineTypeForWriteBackNotesBasedOnDateOfService(
            comment.type,
            date,
            dateOfService,
          ),
        } as AllCommentsProps;
      }

      acc[date].comments.push({
        id: comment.id,
        originalComment: comment.originalComment,
        boStatus: comment.boStatus,
        writeBackComment: comment.writeBackComment,
        showWriteBackNote: comment.showWriteBackNote,
        isVisitDateDifferentFromPostTextDate:
          comment.isVisitDateDifferentFromPostTextDate,
        writeStatus: comment.writeStatus,
      });

      return acc;
    },
    {} as Record<string, AllCommentsProps>,
  );

  return Object.values(groupedRecord);
};

/**
 * Creates a consolidated array of business office notes data by aggregating
 * post notes, pre notes, and visit data into a single structure.
 * Comments are grouped by date and sorted by their original source index.
 *
 * By default, comments with an empty `commentDate` are filtered out. For
 * orgs in `exceptionFlowForUrologyOrgs`, that filter is skipped so empty-date
 * placeholder comments (e.g. an `auth_on_file.reports` entry where only
 * `template_text` / `report_status` are populated) still surface in the UI.
 * Skipping the filter is safe because `groupCommentsByDate` tolerates an
 * empty-string date key — those comments simply fall into a single group
 * under `commentDate: ""`.
 *
 * @param {BusniessOfficeNote[]} [notes] - Array of business office notes to process.
 * @param {string} [templateText] - Optional template text to override the default write-back comment.
 * @param {string} [dateOfService] - The date of service to compare against note dates.
 * @param {string} [orgId] - Optional organization ID used to enable org-specific
 *   overrides (exception-flow orgs bypass the empty-date filter).
 * @returns {AllCommentsProps[]} Array of comment groups (post, pre, visit) grouped by date,
 *                               with appropriate type assignments.
 *
 * @example
 * const notesData = createBusniessOfficeNotesData(notes, "Template", "2024-01-15");
 * // Returns array of { comments, commentDate, type } objects grouped by date
 */
export const createBusniessOfficeNotesData = (
  notes?: BusniessOfficeNote[],
  templateText?: string,
  dateOfService?: string,
  orgId?: string,
): AllCommentsProps[] => {
  const postNotes = createBusniessOfficePostNotes(
    notes,
    templateText,
    dateOfService,
  );
  const preNotes = createBusniessOfficePreNotes(notes);
  const visitData = createBusniessOfficeVisitData(notes, dateOfService);

  const allComments = aggregateAllComments(postNotes, preNotes, visitData);
  const commentsToGroup = isExceptionFlowForUrologyOrg(orgId)
    ? allComments
    : filterCommentsWithDates(allComments);
  const sortedComments = commentsToGroup.sort(
    (a, b) => (a?.index ?? 0) - (b?.index ?? 0),
  );
  const groupedComments = groupCommentsByDate(
    sortedComments,
    dateOfService ?? "",
  );

  return groupedComments;
};

/**
 * Maps a single business office note to a post note comment structure.
 *
 * @param {BusniessOfficeNote} note - The note to transform.
 * @param {string} [templateText] - Optional template text to use as write-back comment.
 * @param {string} [dateOfService] - The date of service for comparison.
 * @returns {CommentItem} The transformed comment item.
 */
const mapNoteToPostComment = (
  note: BusniessOfficeNote,
  templateText?: string,
  dateOfService?: string,
  index?: number,
): CommentItem => {
  const writeBackComment = normalizeAuthDatesToSlashFormat(
    templateText || note.template_text || "",
  );
  const hasPostText = note.post_text !== "";

  return {
    id: note.report_identifier ?? "",
    originalComment: hasPostText
      ? (note.post_text ?? "")
      : "No comments found on OncoEmr for this order",
    boStatus: note.report_status ?? "",
    showWriteBackNote: checkIfDatesAreSame(note.post_text_date, dateOfService),
    writeBackComment,
    commentDate: note.post_text_date ?? "",
    type: BusniessOfficeReportType.POST,
    writeStatus: note.write_status ?? "",
    index,
  };
};

/**
 * Creates business office post notes data structure from raw notes.
 * Maps each note to a comment object with write-back functionality enabled
 * when the post text date matches the date of service.
 *
 * @param {BusniessOfficeNote[]} [notes] - Array of business office notes to process.
 * @param {string} [templateText] - Optional template text to use as the write-back comment.
 * @param {string} [dateOfService] - The date of service to compare against post text dates.
 * @returns {CommentsCollection} Object containing the array of post note comments.
 *
 * @example
 * const postNotes = createBusniessOfficePostNotes(notes, "Template", "2024-01-15");
 * // Returns: { comments: [...] }
 */
export const createBusniessOfficePostNotes = (
  notes?: BusniessOfficeNote[],
  templateText?: string,
  dateOfService?: string,
): CommentsCollection => {
  const postNotesData =
    notes?.map((note, index) =>
      mapNoteToPostComment(note, templateText, dateOfService, index),
    ) ?? [];

  return {
    comments: postNotesData,
  };
};

/**
 * Checks if a note has valid pre-text content.
 *
 * @param {BusniessOfficeNote} note - The note to check.
 * @returns {boolean} True if the note has non-empty pre_text or pre_text_date.
 */
const hasValidPreTextContent = (note: BusniessOfficeNote): boolean => {
  return Boolean(
    (note?.pre_text && note.pre_text.trim()) ||
    (note?.pre_text_date && note.pre_text_date.trim()),
  );
};

/**
 * Maps a single business office note to a pre note comment structure.
 *
 * @param {BusniessOfficeNote} note - The note to transform.
 * @returns {CommentItem} The transformed comment item.
 */
const mapNoteToPreComment = (
  note: BusniessOfficeNote,
  index?: number,
): CommentItem => {
  return {
    id: note.report_identifier ?? "",
    originalComment: note.pre_text ?? "",
    boStatus: "",
    showWriteBackNote: false,
    commentDate: note.pre_text_date ?? "",
    type: BusniessOfficeReportType.PRE,
    writeStatus: note.write_status ?? "",
    index,
  };
};

/**
 * Creates business office pre notes data structure from raw notes.
 * Filters notes to include only those with non-empty pre_text or pre_text_date,
 * then maps them to a simplified comment format without write-back functionality.
 *
 * @param {BusniessOfficeNote[]} [notes] - Array of business office notes to process.
 * @returns {CommentsCollection} Object containing filtered pre-notes comments.
 *                               Returns empty comments array if no valid pre-notes exist.
 *
 * @example
 * const preNotes = createBusniessOfficePreNotes(notes);
 * // Returns: { comments: [...] }
 */
const createBusniessOfficePreNotes = (
  notes?: BusniessOfficeNote[],
): CommentsCollection => {
  const preNotes = notes?.filter(hasValidPreTextContent) ?? [];

  if (preNotes.length === 0) {
    return { comments: [] };
  }

  const preNotesData = preNotes
    .map((note, index) => mapNoteToPreComment(note, index))
    .filter((note) => note.originalComment !== "");

  return { comments: preNotesData };
};

/**
 * Checks if a note has valid visit data content.
 *
 * @param {BusniessOfficeNote} note - The note to check.
 * @returns {boolean} True if the note has non-empty visit_date or visit_date_text.
 */
const hasValidVisitDataContent = (note: BusniessOfficeNote): boolean => {
  return Boolean(
    (note?.visit_date && note.visit_date.trim()) ||
    (note?.visit_date_text && note.visit_date_text.trim()),
  );
};

/**
 * Maps a single business office note to a visit data comment structure.
 *
 * @param {BusniessOfficeNote} note - The note to transform.
 * @param {string} [dateOfService] - The date of service for comparison.
 * @returns {CommentItem} The transformed comment item with visit-specific properties.
 */
const mapNoteToVisitComment = (
  note: BusniessOfficeNote,
  dateOfService?: string,
  index?: number,
): CommentItem => {
  const writeBackComment = normalizeAuthDatesToSlashFormat(
    note?.template_text ?? "",
  );
  const hasVisitDateText = note.visit_date_text !== "";
  const isVisitDateDifferentFromPostTextDate =
    note?.visit_date !== note?.post_text_date;
  const type = checkIfDatesAreSame(note.visit_date, note.post_text_date)
    ? BusniessOfficeReportType.POST
    : BusniessOfficeReportType.VISIT;
  return {
    id: note.report_identifier ?? "",
    originalComment: hasVisitDateText
      ? (note.visit_date_text ?? "")
      : "No comments found on OncoEmr for this order",
    boStatus: note?.report_status ?? "",
    showWriteBackNote: checkIfDatesAreSame(note.visit_date, dateOfService),
    isVisitDateDifferentFromPostTextDate,
    writeBackComment,
    commentDate: note.visit_date ?? "",
    type,
    writeStatus: note.write_status ?? "",
    index,
  };
};

/**
 * Filters visit comments to include only valid entries.
 * A valid entry must have a non-empty original comment and
 * a visit date different from the post text date.
 *
 * @param {CommentItem} comment - The comment to validate.
 * @returns {boolean} True if the comment is valid.
 */
const isValidVisitComment = (comment: CommentItem): boolean => {
  return (
    comment.originalComment !== "" &&
    Boolean(comment.isVisitDateDifferentFromPostTextDate)
  );
};

/**
 * Creates business office visit data structure from raw notes.
 * Filters notes to include only those with non-empty visit_date or visit_date_text,
 * and excludes notes where visit date matches post text date.
 * Write-back is enabled when visit date matches the date of service.
 *
 * @param {BusniessOfficeNote[]} [notes] - Array of business office notes to process.
 * @param {string} [dateOfService] - The date of service to compare against visit dates.
 * @returns {CommentsCollection} Object containing visit comments.
 *                               Returns empty comments array if no valid visit data exists.
 *
 * @example
 * const visitData = createBusniessOfficeVisitData(notes, "2024-01-15");
 * // Returns: { comments: [...] }
 */
const createBusniessOfficeVisitData = (
  notes?: BusniessOfficeNote[],
  dateOfService?: string,
): CommentsCollection => {
  const visitData = notes?.filter(hasValidVisitDataContent) ?? [];

  if (visitData.length === 0) {
    return { comments: [] };
  }

  const visitDataMap = visitData
    .map((note, index) => mapNoteToVisitComment(note, dateOfService, index))
    .filter(isValidVisitComment);
  return { comments: visitDataMap };
};

/**
 * Determines the appropriate report type based on whether the input date
 * matches the date of service.
 *
 * If the dates match, returns FINAL type to indicate the comment is for
 * the current service date. Otherwise, returns the original type.
 *
 * @param {BusniessOfficeReportType} type - The original report type.
 * @param {string} inputDate - The date to compare (comment date).
 * @param {string} dateOfService - The date of service for the order.
 * @returns {BusniessOfficeReportType} FINAL if dates match, otherwise the original type.
 *
 * @example
 * // Returns BusniessOfficeReportType.FINAL when dates match
 * determineTypeForWriteBackNotesBasedOnDateOfService(
 *   BusniessOfficeReportType.POST,
 *   "2024-01-15",
 *   "2024-01-15"
 * );
 *
 * @example
 * // Returns original type when dates don't match
 * determineTypeForWriteBackNotesBasedOnDateOfService(
 *   BusniessOfficeReportType.POST,
 *   "2024-01-14",
 *   "2024-01-15"
 * );
 */
export const determineTypeForWriteBackNotesBasedOnDateOfService = (
  type: BusniessOfficeReportType,
  inputDate: string,
  dateOfService: string,
): BusniessOfficeReportType => {
  const datesMatch = checkIfDatesAreSame(inputDate, dateOfService);

  if (datesMatch) {
    return BusniessOfficeReportType.FINAL;
  }

  return type;
};
